import { useState, useEffect, useContext } from "react";
import { useEffectOnce } from "../hooks/useEffectOnce";
import cookies from "js-cookie";
import i18next from "i18next";
import { Globe } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuItem,
    DropdownMenuContent,
    DropdownMenuSub,
    DropdownMenuSubTrigger,
    DropdownMenuSubContent,
    DropdownMenuPortal,
  } from "./ui/dropdown-menu";
import { directionService } from "../services/directionService";
import LoggerContext from "../context/LoggerProvider";

const availableLanguages = [
    {
      code: "he",
      name: "עברית",
      country_code: "il",
      dir: "rtl",
    },
    {
      code: "en",
      name: "English",
      country_code: "gb",
    },
    {
      code: "de",
      name: "Deutsch",
      country_code: "de",
    },
];

/**
 * Hook that ensures document.body.dir is always set based on the current language.
 * This should be called from a component that ALWAYS mounts (e.g., App.js)
 * so that direction is set even on routes where the Header is hidden.
 */
export function useLanguageDirection() {
    const { Logger } = useContext(LoggerContext);

    useEffect(() => {
        const applyDirection = () => {
            const langCode = cookies.get("i18next")?.split("-")[0] || "en";
            const lang = availableLanguages.find((l) => l.code === langCode);
            const dir = lang?.dir || "ltr";
            if (document.body.dir !== dir) {
                document.body.dir = dir;
                Logger.info("body.dir has been set to " + dir);
                directionService.sendDirectionChange();
            }
        };

        applyDirection();

        // Re-apply when language changes via i18next
        i18next.on("languageChanged", applyDirection);
        return () => {
            i18next.off("languageChanged", applyDirection);
        };
    }, []);
}

/**
 * Hook that manages language state and direction changes.
 * Used by both the standalone Localize component and the LocalizeSubMenu.
 */
function useLocalize() {
    const { Logger } = useContext(LoggerContext);

    const [currentLanguageCode, setCurrentLanguageCode] = useState("");
    const [currentLanguage, setCurrentLanguage] = useState({});
    const [previousLanguage, setPreviousLanguage] = useState({});

    const selectLanguage = (code) => {
        i18next.changeLanguage(code);
        setCurrentLanguageCode(code);
    };

    useEffectOnce(() => {
        setCurrentLanguageCode(cookies.get("i18next")?.split("-")[0] || "en");
        Logger.debug(
            "currentLanguageCode has been set to " +
            (cookies.get("i18next")?.split("-")[0] || "en")
        );
    }, []);

  useEffect(() => {
    document.body.dir = currentLanguage?.dir || "ltr";
    Logger.info("body.dir has been set to " + (currentLanguage?.dir || "ltr"));

    // register change of RTL<->LTR
    if (previousLanguage?.dir !== currentLanguage?.dir) {
      Logger.debug(
        "Script direction change detected, forwarding to design view if necessary"
      );
      directionService.sendDirectionChange();
    }
    setPreviousLanguage(currentLanguage);
  }, [currentLanguage]);

  useEffect(() => {
    let newLanguage = availableLanguages.find(
      (l) => l.code === currentLanguageCode
    );

    setCurrentLanguage(newLanguage);
    Logger.debug("currentLanguage has been set");
  }, [currentLanguageCode]);

  return { selectLanguage, currentLanguageCode };
}

/**
 * Renders language options as a sub-menu inside an existing DropdownMenu.
 * Use this when you want to embed language switching inside a 3-dot menu.
 */
export function LocalizeSubMenu() {
    const { t } = useTranslation();
    const { selectLanguage, currentLanguageCode } = useLocalize();

    return (
        <DropdownMenuSub>
            <DropdownMenuSubTrigger>
                <Globe className="me-2 h-4 w-4" />
                <span>{t("language", "Language")}</span>
            </DropdownMenuSubTrigger>
            <DropdownMenuPortal>
                <DropdownMenuSubContent>
                    {availableLanguages.map((lang, idx) => (
                        <DropdownMenuItem
                            key={idx}
                            onClick={() => selectLanguage(lang.code)}
                        >
                            <span
                                className={`flag-icon mx-2 flag-icon-${lang.country_code}`}
                            />
                            {lang.name}
                            {currentLanguageCode === lang.code && (
                                <span className="ms-auto text-xs text-muted-foreground">✓</span>
                            )}
                        </DropdownMenuItem>
                    ))}
                </DropdownMenuSubContent>
            </DropdownMenuPortal>
        </DropdownMenuSub>
    );
}

/**
 * Standalone language selector with Globe icon trigger.
 * Renders its own DropdownMenu.
 */
function Localize(props) {
    const variant = props?.variant;
    const { selectLanguage } = useLocalize();

    return <>
    <DropdownMenu>
        <DropdownMenuTrigger className={variant === "light" ? "bg-transparent p-1 hover:bg-white/20 rounded-md transition-colors" : "bg-transparent p-1 hover:bg-gray-100 rounded-md transition-colors"}>
            <Globe className={variant === "light" ? "h-5 w-5 text-white" : "h-4 w-4"} />
        </DropdownMenuTrigger>
        <DropdownMenuContent>
            {availableLanguages.map((lang, idx) => (
            <DropdownMenuItem
                key={idx}
                onClick={() => selectLanguage(lang.code)}
            >
                <span
                className={`flag-icon mx-2 flag-icon-${lang.country_code}`}
                />
                {lang.name}
            </DropdownMenuItem>
            ))}
        </DropdownMenuContent>
    </DropdownMenu>
    </>
}

export default Localize;
